import { Box, Collapse, Stack } from '@mui/material';
import { colors, Typography, Button } from '@sweep-io/sweep-design';
import { useState, useRef, useEffect, useMemo } from 'react';
import { SectionBox } from '../../../dependencies/helpers';
import { ExpandButtonSimple } from '../../../../common/ExpandButtonSimple';
import { CenteredCircularProgress } from '../../../../common/CenteredCircularProgress';
import { useSearchContext } from '../../../dependencies/search/SearchContext';
import { OpenAiDescription } from '../../../../parser/ParserTypes';
import LabelChip from '../../../../common/LabelChip';
import { MarkdownWrapper } from '../../../../common/MarkdownWrapper';
import { DataTableVariant } from '../../../../common/table/TableTypes';
import { filterOpenAiContent } from '../../../dependencies/search/searchUtils';
import { useSectionExpansion } from '../../../dependencies/search/useSectionExpansion';
import { HighlightedSection } from '../../../dependencies/search/highlight/HighlightedSection';
import { BASE_SECTION_EXPANSION } from '../../../dependencies/search/consts';

const OpenAiSection = ({ openAiDescription }: { openAiDescription?: OpenAiDescription }) => {
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [isTextClamped, setIsTextClamped] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  const { searchTerm, sectionExpansion } = useSearchContext();

  const { isExpanded, setIsExpanded } = useSectionExpansion({
    searchTerm,
    sectionExpanded: sectionExpansion.openAi,
    defaultExpanded: BASE_SECTION_EXPANSION.openAi,
  });

  const { text: rawText, type } = openAiDescription ?? {};

  // Filter the content to remove first h4 (and its content) and second h4 heading
  const text = useMemo(() => {
    return rawText ? filterOpenAiContent(rawText) : rawText;
  }, [rawText]);

  // Auto-expand text when search results appear in OpenAI section
  useEffect(() => {
    if (searchTerm && sectionExpansion.openAi) {
      setIsTextExpanded(true);
    } else if (!searchTerm) {
      setIsTextExpanded(false);
    }
  }, [searchTerm, sectionExpansion.openAi]);

  useEffect(() => {
    if (text && !isTextExpanded) {
      // Character-based estimation
      const avgCharsPerLine = 75;
      const estimatedLines = text.length / avgCharsPerLine;
      const estimatedNeedsButton = estimatedLines > 12;

      // DOM-based detection with delay
      const checkDOM = () => {
        let domNeedsButton = false;
        if (textRef.current) {
          const element = textRef.current;
          domNeedsButton = element.scrollHeight > element.clientHeight;
        }

        // Use either method - character estimation OR DOM detection
        const shouldShowButton = estimatedNeedsButton || domNeedsButton;
        setIsTextClamped(shouldShowButton);
      };

      // Check with delay to ensure rendering is complete
      setTimeout(checkDOM, 100);
    } else {
      setIsTextClamped(false);
    }
  }, [text, isTextExpanded, isExpanded]);

  return (
    <SectionBox data-section="openAi">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{ cursor: 'pointer', width: '100%' }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h5-medium">Overview</Typography>
        </Box>
        <Stack alignItems="center" gap={1} direction="row">
          <LabelChip bgColor={colors.grey[100]}>AI generated</LabelChip>
          <ExpandButtonSimple buttonSize="tiny" isExpanded={isExpanded} />
        </Stack>
      </Box>

      <Collapse in={isExpanded}>
        <Box pt={'12px'}>
          {!text && <CenteredCircularProgress size={20} />}

          {text && (
            <HighlightedSection section="openAi">
              <Box
                sx={{
                  '& span > p:first-of-type': {
                    m: 0,
                    p: 0,
                  },

                  '& span > h4:nth-of-type(2) + p': {
                    m: 0,
                    p: 0,
                  },
                }}
              >
                <Typography
                  variant="body"
                  color={type === 'error' ? colors.grey[500] : colors.grey[800]}
                >
                  <Box
                    ref={textRef}
                    component="span"
                    sx={{
                      // Apply line-clamp directly to the content wrapper
                      display: isTextExpanded ? 'inline' : '-webkit-box',
                      WebkitLineClamp: isTextExpanded ? 'unset' : 9,
                      WebkitBoxOrient: isTextExpanded ? 'unset' : 'vertical',
                      overflow: isTextExpanded ? 'visible' : 'hidden',
                      // Remove textOverflow - not needed with WebkitLineClamp
                      // Force word breaking for long strings without spaces
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  >
                    <MarkdownWrapper
                      component="span"
                      content={text}
                      removeMargins={false}
                      dataTableVariant={DataTableVariant.small}
                    />
                  </Box>
                </Typography>
              </Box>
            </HighlightedSection>
          )}
        </Box>
        {type !== 'error' && (isTextClamped || isTextExpanded) && (
          <Box mt={1}>
            <Button
              variant="flat-blue"
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering the section collapse
                setIsTextExpanded(!isTextExpanded);
              }}
            >
              {isTextExpanded ? 'Show less' : 'Show more'}
            </Button>
          </Box>
        )}
      </Collapse>
    </SectionBox>
  );
};

export default OpenAiSection;
